/**
 * `graphApi` — standalone RTK Query slice for the graph explorer,
 * and lightweight manual node edits on the Graph page.
 *
 * Split off from `ontologyApi` so graph reads/writes own their own cache
 * + tag space (`'Graph'`, `'Nodes'`) instead of sharing the catch-all
 * `ontologyApi` tag soup. Shares `ontologyBaseQuery` (auth + branch
 * headers, `/api/v1/ontology` root) with the other ontology slices via
 * `ontology-api-base.ts`.
 *
 * Cross-slice invalidation is centralized in `cache-coherence-bridge.ts`;
 * context switches use the same `invalidateGraphData` fan-out.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import {
  drainCypherRows,
  ontologyBaseQuery,
  type ClearHistorianResult,
  type ClearNeo4jResult,
  type ClearPostgresResult,
  type CypherDrainBaseQuery,
  type CypherReadResult,
  type CypherWriteResult,
  type PostgresQueryArgs,
  type PostgresQueryResult
} from '../core'
import type { Branch } from '../branches/types'
import type { TagBundleImportRequest, TagBundleImportResult } from './download-tag-bundle'
import {
  FLAGGED_NODES_CYPHER,
  mapFlaggedNodeRow,
  type FlaggedNode,
  type RecordNodeEvidenceArgs,
  type RecordNodeEvidenceResult
} from './flagged-nodes'
import {
  LOW_CONFIDENCE_NODES_CYPHER,
  LOW_CONFIDENCE_RELATIONSHIPS_CYPHER,
  mapLowConfidenceNodeRow,
  mapLowConfidenceRelationshipRow,
  type LowConfidenceNode,
  type LowConfidenceQueryArgs,
  type LowConfidenceRelationship,
  type RecordRelationshipEvidenceArgs,
  type RecordRelationshipEvidenceResult
} from './low-confidence'
import {
  REVIEW_QUEUE_NODES_CYPHER,
  REVIEW_QUEUE_RELATIONSHIPS_CYPHER,
  mapReviewQueueNodeRow,
  mapReviewQueueRelationshipRow,
  type ReviewQueueNode,
  type ReviewQueueRelationship
} from './review-queue'
import type {
  GraphCypherQueryResult,
  GraphQueryParams,
  GraphResponse,
  ManualUpdateNodeInput,
  OntologyGraphNode
} from './types'

export const GRAPH_TAG_TYPES = [
  'Graph',
  'Nodes',
  'FlaggedNodes',
  'LowConfidence',
  'ReviewQueue'
] as const
export type GraphTagType = (typeof GRAPH_TAG_TYPES)[number]

export interface GraphChangeTokenResponse {
  token: string
}

export const graphApi = createApi({
  reducerPath: 'graphApi',
  baseQuery: ontologyBaseQuery,
  refetchOnReconnect: true,
  tagTypes: GRAPH_TAG_TYPES,
  endpoints: (builder) => ({
    // branchKey scopes RTK's cache; the request itself is routed by the active
    // X-Ontology-Branch header attached by ontologyBaseQuery.
    getGraphChangeToken: builder.query<GraphChangeTokenResponse, string>({
      query: () => '/graph/change-token'
    }),
    getGraph: builder.query<GraphResponse, GraphQueryParams | void>({
      query: (params) => ({
        url: '/graph',
        params: params ?? undefined
      }),
      providesTags: ['Graph']
    }),
    getNodeDetails: builder.query<GraphResponse['nodes'][number], string>({
      query: (nodeId) => `/graph/nodes/${nodeId}`,
      providesTags: (_result, _error, nodeId) => [{ type: 'Graph', id: nodeId }]
    }),
    searchGraph: builder.query<GraphResponse['nodes'], string>({
      query: (query) => ({
        url: '/graph/search',
        params: { q: query }
      }),
      providesTags: ['Graph']
    }),
    runCypher: builder.mutation<
      GraphCypherQueryResult,
      { query: string; params?: Record<string, unknown> }
    >({
      query: (body) => ({
        url: '/cypher/read',
        method: 'POST',
        body
      })
    }),
    runCypherWrite: builder.mutation<
      CypherWriteResult,
      { query: string; params?: Record<string, unknown> }
    >({
      query: (body) => ({
        url: '/cypher/write',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Graph', 'Nodes', 'FlaggedNodes', 'LowConfidence', 'ReviewQueue']
    }),
    updateManualNode: builder.mutation<OntologyGraphNode, { id: string } & ManualUpdateNodeInput>({
      query: ({ id, ...body }) => ({
        url: `/nodes/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Nodes',
        'Graph',
        'FlaggedNodes',
        'LowConfidence',
        'ReviewQueue',
        { type: 'Nodes', id }
      ]
    }),
    deleteManualNode: builder.mutation<void, string>({
      query: (id) => ({
        url: `/nodes/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, id) => [
        'Nodes',
        'Graph',
        'FlaggedNodes',
        'LowConfidence',
        'ReviewQueue',
        { type: 'Nodes', id }
      ]
    }),
    deleteManualRelationship: builder.mutation<void, string>({
      query: (id) => ({
        url: `/relationships/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Nodes', 'Graph', 'FlaggedNodes', 'LowConfidence', 'ReviewQueue']
    }),
    // Flagged (`valid = false`) nodes for the Settings "Graph properties"
    // surface, with the reason from each node's latest manual evidence.
    listFlaggedNodes: builder.query<FlaggedNode[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          FLAGGED_NODES_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const out: FlaggedNode[] = []
        for (const row of drained.data?.results ?? []) {
          const node = mapFlaggedNodeRow(row)
          if (node) out.push(node)
        }
        return { data: out, meta: drained.meta }
      },
      providesTags: ['FlaggedNodes']
    }),
    listLowConfidenceNodes: builder.query<LowConfidenceNode[], LowConfidenceQueryArgs>({
      queryFn: async (arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/cypher/read',
          method: 'POST',
          body: {
            query: LOW_CONFIDENCE_NODES_CYPHER,
            params: { threshold: arg.threshold, limit: arg.limit }
          }
        })
        if (result.error) return { error: result.error, meta: result.meta }
        const payload = (result.data ?? {}) as Partial<CypherReadResult>
        const out: LowConfidenceNode[] = []
        for (const row of payload.results ?? []) {
          const node = mapLowConfidenceNodeRow(row)
          if (node) out.push(node)
        }
        return { data: out, meta: result.meta }
      },
      providesTags: ['LowConfidence']
    }),
    listLowConfidenceRelationships: builder.query<
      LowConfidenceRelationship[],
      LowConfidenceQueryArgs
    >({
      queryFn: async (arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/cypher/read',
          method: 'POST',
          body: {
            query: LOW_CONFIDENCE_RELATIONSHIPS_CYPHER,
            params: { threshold: arg.threshold, limit: arg.limit }
          }
        })
        if (result.error) return { error: result.error, meta: result.meta }
        const payload = (result.data ?? {}) as Partial<CypherReadResult>
        const out: LowConfidenceRelationship[] = []
        for (const row of payload.results ?? []) {
          const rel = mapLowConfidenceRelationshipRow(row)
          if (rel) out.push(rel)
        }
        return { data: out, meta: result.meta }
      },
      providesTags: ['LowConfidence']
    }),
    listReviewQueueNodes: builder.query<ReviewQueueNode[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          REVIEW_QUEUE_NODES_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const out: ReviewQueueNode[] = []
        for (const row of drained.data?.results ?? []) {
          const node = mapReviewQueueNodeRow(row)
          if (node) out.push(node)
        }
        return { data: out, meta: drained.meta }
      },
      providesTags: ['ReviewQueue']
    }),
    listReviewQueueRelationships: builder.query<ReviewQueueRelationship[], void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const drained = await drainCypherRows(
          baseQuery as CypherDrainBaseQuery,
          REVIEW_QUEUE_RELATIONSHIPS_CYPHER
        )
        if (drained.error) return { error: drained.error, meta: drained.meta }
        const out: ReviewQueueRelationship[] = []
        for (const row of drained.data?.results ?? []) {
          const rel = mapReviewQueueRelationshipRow(row)
          if (rel) out.push(rel)
        }
        return { data: out, meta: drained.meta }
      },
      providesTags: ['ReviewQueue']
    }),
    // Direct manual write: merges a `manual` provenance evidence entry and
    // (optionally) asserts `valid`. Used by the re-validate affordance —
    // a deliberate human act, so it calls the endpoint directly rather
    // than riding the agent propose→accept rail.
    recordNodeEvidence: builder.mutation<RecordNodeEvidenceResult, RecordNodeEvidenceArgs>({
      query: (body) => ({
        url: '/nodes/evidence',
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, { nodeId }) => [
        'FlaggedNodes',
        'LowConfidence',
        'ReviewQueue',
        'Nodes',
        'Graph',
        { type: 'Nodes', id: nodeId }
      ]
    }),
    recordRelationshipEvidence: builder.mutation<
      RecordRelationshipEvidenceResult,
      RecordRelationshipEvidenceArgs
    >({
      query: (body) => ({
        url: '/relationships/evidence',
        method: 'POST',
        body
      }),
      invalidatesTags: ['FlaggedNodes', 'LowConfidence', 'ReviewQueue', 'Nodes', 'Graph']
    }),
    // Debug-only destructive reset, surfaced in Settings only while debug mode
    // is enabled. Stores are cleared via independent endpoints; the caller
    // wipes Neo4j first so a branch-not-open failure leaves Postgres and
    // historian intact, then historian last so a Timescale failure still
    // leaves the control-plane wipe committed.
    clearPostgres: builder.mutation<ClearPostgresResult, void>({
      query: () => ({
        url: '/maintenance/clear-postgres',
        method: 'POST'
      })
    }),
    clearNeo4j: builder.mutation<ClearNeo4jResult, void>({
      query: () => ({
        url: '/maintenance/clear-neo4j',
        method: 'POST'
      }),
      invalidatesTags: ['Graph', 'Nodes', 'FlaggedNodes', 'LowConfidence', 'ReviewQueue']
    }),
    clearHistorian: builder.mutation<ClearHistorianResult, void>({
      query: () => ({
        url: '/maintenance/clear-historian',
        method: 'POST'
      })
    }),
    /** Debug SQL against the proxy's own ontology Postgres (Settings → Databases). */
    executePostgresQuery: builder.mutation<PostgresQueryResult, PostgresQueryArgs>({
      query: (body) => ({
        url: '/maintenance/postgres-query',
        method: 'POST',
        body: {
          query: body.query,
          write: body.write === true
        }
      })
    }),
    importNeo4jBackup: builder.mutation<Branch, { name: string; file: File }>({
      query: ({ name, file }) => {
        const form = new FormData()
        form.append('name', name.trim())
        form.append('file', file)
        return {
          url: '/maintenance/neo4j-backup-import',
          method: 'POST',
          body: form
        }
      }
    }),
    importPostgresBackup: builder.mutation<{ imported: boolean }, { file: File }>({
      query: ({ file }) => {
        const form = new FormData()
        form.append('file', file)
        return {
          url: '/maintenance/postgres-backup-import',
          method: 'POST',
          body: form
        }
      }
    }),
    importTagBundle: builder.mutation<TagBundleImportResult, TagBundleImportRequest>({
      query: ({ file, mode, dryRun, urlRewrites, applyToGraph }) => {
        const form = new FormData()
        form.append('file', file)
        if (mode) form.append('mode', mode)
        if (dryRun) form.append('dryRun', 'true')
        for (const rule of urlRewrites ?? []) {
          form.append('urlRewrites', `${rule.from}=${rule.to}`)
        }
        // Sent only when the operator overrode it; absent means the server's
        // default (on whenever a rewrite map is supplied).
        if (applyToGraph !== undefined) form.append('applyToGraph', String(applyToGraph))
        return {
          url: '/maintenance/tag-bundle-import',
          method: 'POST',
          body: form
        }
      }
    })
  })
})

export const {
  useGetGraphChangeTokenQuery,
  useGetGraphQuery,
  useGetNodeDetailsQuery,
  useLazySearchGraphQuery,
  useRunCypherMutation,
  useRunCypherWriteMutation,
  useDeleteManualNodeMutation,
  useDeleteManualRelationshipMutation,
  useUpdateManualNodeMutation,
  useListFlaggedNodesQuery,
  useListLowConfidenceNodesQuery,
  useListLowConfidenceRelationshipsQuery,
  useListReviewQueueNodesQuery,
  useListReviewQueueRelationshipsQuery,
  useRecordNodeEvidenceMutation,
  useRecordRelationshipEvidenceMutation,
  useClearPostgresMutation,
  useClearNeo4jMutation,
  useClearHistorianMutation,
  useExecutePostgresQueryMutation,
  useImportNeo4jBackupMutation,
  useImportPostgresBackupMutation,
  useImportTagBundleMutation
} = graphApi
